import {
  applySnapshot,
  cast,
  detach,
  flow,
  getSnapshot,
  Instance,
  isStateTreeNode,
  types,
} from 'mobx-state-tree';
import { splitPhone } from 'utils';
import { ISector, ITenantRating, ITenantRatingsData } from '../../types';
import { Requests, Tenants } from '../../http-requests';

import { RequestStateModel } from './general-models';

export interface ITenant extends Instance<typeof TenantModel> {}

const StatusesModel = types.model({
  tenantRating: types.optional(RequestStateModel, {}),
  tenantsList: types.optional(RequestStateModel, {}),
  tenantRequestsRating: types.optional(RequestStateModel, {}),
  notification: types.optional(RequestStateModel, {}),
  rate: types.optional(RequestStateModel, {}),
  stats: types.optional(RequestStateModel, {}),
  sectors: types.optional(RequestStateModel, {}),
  calls: types.optional(RequestStateModel, {}),
  tickets: types.optional(RequestStateModel, {}),
  socialNetworks: types.optional(RequestStateModel, {}),
});

const SectorModel = types.model({
  code: types.optional(types.string, ''),
  value: types.optional(types.number, 0),
});

const TenantHouseModel = types.model('TenantHouseModel', {
  id: types.optional(types.string, ''),
  address: types.optional(types.string, ''),
  fias_addrobjs: types.optional(types.array(types.string), []),
});

const TenantAreaModel = types
  .model('TenantAreaModel', {
    id: types.optional(types.string, ''),
    house: types.optional(TenantHouseModel, {}),
    is_shared: types.optional(types.maybeNull(types.boolean), false),
    number: types.optional(types.number, 0),
    str_number: types.optional(types.string, ''),
    str_number_full: types.optional(types.string, ''),
    _type: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    get fullAddress() {
      return `${self.house.address}, ${self.str_number_full}`;
    },
  }));

const TenantPhoneBaseModel = types.model('TenantPhoneBase', {
  id: types.optional(types.identifier, ''),
  add: types.optional(types.maybeNull(types.string), null),
  added_by_tenant: types.optional(types.boolean, false),
  code: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  not_actual: types.optional(types.boolean, false),
  phone_type: types.optional(types.string, ''),
  str_number: types.optional(types.maybeNull(types.string), null),
  viber: types.optional(types.boolean, false),
  whatsapp: types.optional(types.boolean, false),
  telegram: types.optional(types.boolean, false),
});

const TenantPhoneModel = types.snapshotProcessor(TenantPhoneBaseModel, {
  preProcessor(sn: Partial<Instance<typeof TenantPhoneBaseModel>>) {
    return {
      ...sn,
      id: sn.id ?? String(Date.now()),
      viber: !!sn.viber,
      whatsapp: !!sn.whatsapp,
      telegram: !!sn.telegram,
    };
  },
  postProcessor(sn) {
    return sn;
  },
});

const TenantModel = types.model('TenantModel', {
  id: types.optional(types.identifier, ''),
  area: types.optional(TenantAreaModel, {}),
  email: types.optional(types.maybeNull(types.string), null),
  inn: types.optional(types.maybeNull(types.string), null),
  name: types.optional(types.maybeNull(types.string), null),
  number: types.optional(types.string, ''),
  last_name_upper: types.optional(types.maybeNull(types.string), null),
  short_name: types.optional(types.string, ''),
  short_name_upper: types.optional(types.maybeNull(types.string), null),
  str_name: types.optional(types.string, ''),
  phones: types.optional(types.array(TenantPhoneModel), []),
  sectors: types.optional(types.frozen(), []),
  requests: types.optional(types.frozen(), {}),
  has_access: types.optional(types.boolean, false),
  telegram_chats: types.optional(types.frozen(), []),
});

const SOCIAL_CHANNELS = [
  'max',
  'vkontakte',
  'odnoklassniki',
  'telegram',
] as const;

export type SocialChannel = (typeof SOCIAL_CHANNELS)[number];

const SocialsEntryModel = types.model('SocialsEntry', {
  max: types.optional(types.boolean, false),
  vkontakte: types.optional(types.boolean, false),
  odnoklassniki: types.optional(types.boolean, false),
  telegram: types.optional(types.boolean, false),
  fetch: types.optional(RequestStateModel, {}),
  update: types.optional(RequestStateModel, {}),
});

export const TenantsStore = types
  .model({
    current: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    currentRate: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    tenantRatings: types.optional(
      types.frozen<Record<string, ITenantRating>>(),
      {}
    ),
    tenantsList: types.optional(types.array(TenantModel), []),
    areasList: types.optional(types.array(TenantAreaModel), []),
    currentTenant: types.optional(types.safeReference(TenantModel), ''),
    tenantRequestsRating: types.optional(
      types.frozen<{ [key: string]: any }>(),
      {}
    ),
    statuses: types.optional(StatusesModel, {}),
    tenantStats: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    sectors: types.optional(types.array(SectorModel), []),
    calls: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    tickets: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    socialsCache: types.map(SocialsEntryModel),
  })
  .volatile(() => ({
    tenantSectorsResultsGeneration: 0,
    tenantSectorsLoadingGeneration: 0,
  }))
  .views((self) => ({
    getSectorsData(code: string): number | undefined {
      const data: ISector | undefined = self.sectors?.find(
        (item: ISector): boolean => item.code === code
      );

      return data?.value;
    },
    get sectorsTotalDebt(): number {
      return self.sectors.reduce((acc, curr) => (acc += curr.value), 0);
    },
    get communicationChannels(): { [key: string]: any } {
      return {
        app: !!self.tenantStats.has_access,
        chat_bot:
          !!self.tenantStats?.phones_connected_to_tg_bot?.length ||
          !!self.tenantStats?.phones_connected_to_max_bot?.length,
        email: !!self.tenantStats?.email?.length,
        vk: !!self.tenantStats?.phones?.find(
          (phone: any) => phone.telegram || phone.odnoklassniki
        ),
      };
    },
    getTenantRatingById(tenantId: string): ITenantRating | undefined {
      return self.tenantRatings?.[tenantId];
    },
    getSocialsEntry(phone: string) {
      return self.socialsCache.get(phone);
    },
  }))
  .actions((self) => {
    const resetPlainState = () => {
      self.current = {};
      self.currentRate = {};
      self.tenantRatings = {};
      self.currentTenant = undefined;
      self.tenantRequestsRating = {};
      self.tenantStats = {};
      self.calls = {};
      self.tickets = {};
    };

    const detachTenantsAndAreas = () => {
      [...self.areasList.slice(), ...self.tenantsList.slice()].forEach(
        (node) => {
          detach(node);
        }
      );
    };

    return {
      setAreas(newList: Array<ITenant['area']>) {
        self.areasList.replace(
          newList.map((area) =>
            isStateTreeNode(area) ? getSnapshot(area) : area
          ) as Array<ITenant['area']>
        );
      },
      clearTenantStats() {
        self.tenantStats = {};
      },
      clearTenantSectors() {
        self.tenantSectorsResultsGeneration += 1;
        self.sectors = cast([]);
      },
      clearAll() {
        self.tenantSectorsResultsGeneration += 1;
        self.tenantSectorsLoadingGeneration += 1;
        resetPlainState();
        detachTenantsAndAreas();
        applySnapshot(self.statuses, {});
        self.sectors.clear();
        self.socialsCache.clear();
      },
      newRateTenant: flow(function* (
        tenantID: string,
        rate: 'up' | 'down' | null
      ) {
        try {
          self.statuses.rate.loadingStates();
          const response = yield Tenants.RateTenantNEW(tenantID, { rate });
          self.tenantRatings = {
            ...self.tenantRatings,
            [tenantID]: response,
          };
          self.statuses.rate.successStates();

          return response;
        } catch (error) {
          self.statuses.rate.errorStates();
          console.error(error);

          throw error;
        } finally {
          self.statuses.rate.finallyStates();
        }
      }),
      postNotificationSend: flow(function* (data: { [key: string]: any }) {
        try {
          self.statuses.notification.fulfilled = false;
          self.statuses.notification.error = false;
          self.statuses.notification.pending = true;

          const response = yield Tenants.PostNotificationSend(data);

          self.statuses.notification.fulfilled = true;

          return response;
        } catch (e) {
          self.statuses.notification.error = true;
          console.error(e);
        } finally {
          self.statuses.notification.pending = false;
        }
      }),
      getTenantsList: flow(function* (strPhone: string) {
        try {
          self.statuses.tenantsList.loadingStates();
          const phone = splitPhone(strPhone);
          const response = yield Tenants.GetTenantsCardsByPhone({
            phones__str_number: phone.code + phone.number,
          });
          self.tenantsList.replace(response.results);
          const uniqueAreas = Array.from(
            new Map(
              response.results.map((tenant: ITenant) => [
                tenant.area.id,
                tenant.area,
              ])
            ).values()
          );
          self.areasList.replace(uniqueAreas as Array<ITenant['area']>);
          self.statuses.tenantsList.successStates();

          return self.tenantsList;
        } catch (error) {
          self.statuses.tenantsList.errorStates();
          console.error(error);
        } finally {
          self.statuses.tenantsList.finallyStates();
        }
      }),
      getTenantStats: flow(function* (tenantId: string) {
        try {
          self.statuses.stats.loadingStates();

          const response = yield Tenants.GetTenantStats(tenantId);
          self.tenantStats = response;
          self.statuses.stats.successStates();

          return response;
        } catch (e) {
          self.statuses.stats.errorStates();
          console.error(e);
        } finally {
          self.statuses.stats.finallyStates();
        }
      }),
      getTenantRequestsRating: flow(function* (tenantId: string) {
        self.statuses.tenantRequestsRating.loadingStates();

        try {
          const response: { [key: string]: ITenantRatingsData } =
            // TODO неверный эндпоинт, на бэке по данному функционалу рудимент, будут перерабатывать 
            yield Requests.GetTenantRequestRating(tenantId);

          self.tenantRequestsRating = {
            ...self.tenantRequestsRating,
            [tenantId]: response,
          };

          self.statuses.tenantRequestsRating.successStates();
        } catch (e) {
          self.statuses.tenantRequestsRating.errorStates();
          console.error(e);
        } finally {
          self.statuses.tenantRequestsRating.finallyStates();
        }
      }),
      getTenantRating: flow(function* (tenantID: string) {
        try {
          self.statuses.tenantRating.loadingStates();

          const response: ITenantRating =
            yield Tenants.GetTenantRating(tenantID);
          self.tenantRatings = {
            ...self.tenantRatings,
            [tenantID]: response,
          };
          self.statuses.tenantRating.successStates();

          return response;
        } catch (error) {
          self.statuses.tenantRating.errorStates();
          console.error(error);

          throw error;
        } finally {
          self.statuses.tenantRating.finallyStates();
        }
      }),
      getTenantSectors: flow(function* (
        tenantId: string,
        shouldApply?: () => boolean
      ) {
        const resultsGeneration = self.tenantSectorsResultsGeneration;
        const loadingGeneration = self.tenantSectorsLoadingGeneration;
        const canApply = () =>
          resultsGeneration === self.tenantSectorsResultsGeneration &&
          (shouldApply?.() ?? true);
        const canFinalize = () =>
          loadingGeneration === self.tenantSectorsLoadingGeneration;

        try {
          self.statuses.sectors.loadingStates();
          const response: { sectors: ISector[] } =
            yield Tenants.GetTenantSectors(tenantId);

          if (canApply()) {
            self.sectors.replace(response.sectors);
            self.statuses.sectors.successStates();
          }

          return self.sectors;
        } catch (e) {
          if (canApply()) {
            self.statuses.sectors.errorStates();
            console.error(e);
          }
        } finally {
          if (canFinalize()) {
            self.statuses.sectors.finallyStates();
          }
        }
      }),
      getTenantCallStats: flow(function* (tenantId: string) {
        try {
          self.statuses.calls.loadingStates();
          const response = yield Tenants.GetTenantCallsStats(tenantId);
          self.calls = response;
          self.statuses.calls.successStates();

          return response;
        } catch (e) {
          self.statuses.calls.errorStates();
          console.error(e);
        } finally {
          self.statuses.calls.finallyStates();
        }
      }),
      getTenantTicketStats: flow(function* (tenantId) {
        try {
          self.statuses.tickets.loadingStates();
          const response = yield Tenants.GetTenantTicketsStats(tenantId);
          self.tickets = response;
          self.statuses.tickets.successStates();

          return response;
        } catch (e) {
          self.statuses.tickets.errorStates();
          console.error(e);
        } finally {
          self.statuses.tickets.finallyStates();
        }
      }),
      getSocialNetworks: flow(function* (phone_number: string) {
        try {
          self.statuses.socialNetworks.loadingStates();
          const response = yield Tenants.GetNumberSocialNetworks({
            phone_number,
          });
          self.statuses.socialNetworks.successStates();

          return response;
        } catch (error) {
          self.statuses.socialNetworks.errorStates();

          throw error;
        } finally {
          self.statuses.socialNetworks.finallyStates();
        }
      }),
      updateSocialNetwork: flow(function* (params: {
        phone_number: string;
        viber: boolean;
        telegram: boolean;
        whatsapp: boolean;
      }) {
        try {
          self.statuses.socialNetworks.loadingStates();
          const response = yield Tenants.PostNumberSocialNetworks(params);
          self.statuses.socialNetworks.successStates();

          return response;
        } catch (error) {
          self.statuses.socialNetworks.errorStates();

          throw error;
        } finally {
          self.statuses.socialNetworks.finallyStates();
        }
      }),
      fetchSocials: flow(function* (phone: string) {
        if (!self.socialsCache.has(phone)) {
          self.socialsCache.set(phone, {});
        }

        const entry = self.socialsCache.get(phone)!;

        if (entry.fetch.pending) {
          return;
        }

        entry.fetch.loadingStates();

        try {
          const res: Record<string, boolean> =
            yield Tenants.GetNumberSocialNetworks({ phone_number: phone });

          for (const key of SOCIAL_CHANNELS) {
            if (key in res) {
              entry[key] = !!res[key];
            }
          }

          entry.fetch.successStates();
        } catch (error) {
          entry.fetch.errorStates();
          console.error(error);
        } finally {
          entry.fetch.finallyStates();
        }
      }),
      toggleSocial: flow(function* (phone: string, channel: SocialChannel) {
        const entry = self.socialsCache.get(phone);

        if (!entry || entry.fetch.pending || entry.update.pending) {
          return;
        }

        entry[channel] = !entry[channel];
        entry.update.loadingStates();

        try {
          yield Tenants.PostNumberSocialNetworks({
            phone_number: phone,
            max: entry.max,
            vkontakte: entry.vkontakte,
            odnoklassniki: entry.odnoklassniki,
            telegram: entry.telegram,
          });

          entry.update.successStates();
        } catch (error) {
          entry[channel] = !entry[channel];
          entry.update.errorStates();
          console.error(error);

          return Promise.reject(error);
        } finally {
          entry.update.finallyStates();
        }
      }),
      clearSocialsCache() {
        self.socialsCache.clear();
      },
      // TODO: был перенесен из requestsStore.  Сейчас не используется, но может понадобится
      // когда на бэке будет решена задача по получению статистики жителя
      // setTenantStatsVote(rate: string) {
      //   const newRate: { [key: string]: number } = {};
      //   // let rateType: string = good ? 'up' : 'down';
      //   newRate[rate] =
      //     (self.tenantStats.assessment
      //       ? self.tenantStats.assessment['rateType']
      //       : 0) + 1;

      //   self.tenantStats = {
      //     ...self.tenantStats,
      //     can_vote: false,
      //     assessment: {
      //       ...self.tenantStats.assessment,
      //       ...newRate,
      //     },
      //   };
      // },
    };
  })
  .actions((self) => {
    const state = { inFlight: 0, queue: [] as string[] };

    const processQueue = () => {
      while (state.inFlight < 5 && state.queue.length > 0) {
        const phone = state.queue.shift()!;
        const entry = self.socialsCache.get(phone);

        if (
          !entry ||
          entry.fetch.fulfilled ||
          entry.fetch.pending ||
          entry.fetch.error
        ) {
          continue;
        }

        state.inFlight++;

        self.fetchSocials(phone).finally(() => {
          state.inFlight--;
          processQueue();
        });
      }
    };

    return {
      loadSocials(phone: string) {
        if (!/^\d{10,}$/.test(phone)) {
          return;
        }

        const entry = self.socialsCache.get(phone);

        if (
          entry?.fetch.fulfilled ||
          entry?.fetch.pending ||
          entry?.fetch.error
        ) {
          return;
        }

        if (!self.socialsCache.has(phone)) {
          self.socialsCache.set(phone, {});
        }

        if (!state.queue.includes(phone)) {
          state.queue.push(phone);
        }

        processQueue();
      },

      invalidateSocials(phone: string) {
        const entry = self.socialsCache.get(phone);

        if (entry) {
          entry.fetch.clearStates();
        }

        if (!state.queue.includes(phone)) {
          state.queue.push(phone);
        }

        processQueue();
      },
    };
  });
